import { Alert, Anchor, Badge, Group, Stack, Table, Text, Title } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useOrgs } from '../api/orgs';
import { usePatientMergeHistory } from '../api/patient-merge';
import { usePatient } from '../api/patients';
import { PatientAttributesCard } from '../components/patient-attributes-card';
import { PatientMergeHistory } from '../components/patient-merge-history';

/**
 * Per-patient detail page reached from the Patients list. Shows a read-only
 * identity header and hosts the (super-admin-only) editable attributes card.
 */
export function PatientDetailPage() {
  const { id = '' } = useParams();
  const query = usePatient(id);
  const { data: orgs } = useOrgs();

  const orgName = useMemo(() => {
    const p = query.data;
    if (!p) return '';
    return (orgs ?? []).find((o) => o.id === p.organisation_id)?.name ?? p.organisation_id;
  }, [orgs, query.data]);

  const p = query.data;
  const mergeHistory = usePatientMergeHistory(p?.id, p?.organisation_id);

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Title order={2}>{p ? `${p.given_name} ${p.family_name}` : 'Patient'}</Title>
        {p && (
          <Anchor
            component={Link}
            to={`/cases?patient_id=${encodeURIComponent(p.id)}&org_id=${encodeURIComponent(p.organisation_id)}`}
          >
            View cases
          </Anchor>
        )}
      </Group>

      {query.isError ? (
        <Text c="red">Could not load patient.</Text>
      ) : !p ? (
        <Text c="dimmed">Loading patient…</Text>
      ) : (
        <>
          {mergeHistory.data?.merged_into && (
            <Alert color="yellow" icon={<IconAlertTriangle size={18} />}>
              This record was merged into{' '}
              <Anchor
                component={Link}
                to={`/patients/${encodeURIComponent(mergeHistory.data.merged_into.surviving_patient_id)}`}
              >
                {mergeHistory.data.merged_into.surviving_name ??
                  mergeHistory.data.merged_into.surviving_patient_id}
              </Anchor>{' '}
              on {new Date(mergeHistory.data.merged_into.merged_at).toLocaleString()} by{' '}
              {mergeHistory.data.merged_into.actor.display}
              {mergeHistory.data.merged_into.reason
                ? ` — ${mergeHistory.data.merged_into.reason}`
                : ''}
              .
            </Alert>
          )}

          <Group gap="xl" wrap="wrap">
            <div>
              <Text size="xs" c="dimmed">
                Organisation
              </Text>
              <Text>{orgName}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">
                Date of birth
              </Text>
              <Text>{p.dob}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">
                Status
              </Text>
              <Badge color={p.status === 'active' ? 'green' : 'gray'} variant="light">
                {p.status}
              </Badge>
            </div>
          </Group>

          {p.identifiers.length > 0 && (
            <div>
              <Text size="xs" c="dimmed" mb={4}>
                Identifiers
              </Text>
              <Table withTableBorder w="auto">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Scheme</Table.Th>
                    <Table.Th>Value</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {p.identifiers.map((idf) => (
                    <Table.Tr key={`${idf.scheme}:${idf.value}`}>
                      <Table.Td>{idf.scheme}</Table.Td>
                      <Table.Td>{idf.value}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          )}

          <PatientAttributesCard patientId={p.id} />
          <PatientMergeHistory patientId={p.id} orgId={p.organisation_id} />
        </>
      )}
    </Stack>
  );
}
